import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableContainer, TableHead, TableRow } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import UpIcon from '@material-ui/icons/KeyboardArrowUp';
import { useIntl } from 'react-intl';
import { Redirect } from 'react-router-dom';
import LimpiarFiltros from '../../assets/images/limpiar-filtros.svg';
import { StyledTooltip } from '../../commons/utils/TableStyles.js';
import * as Utils from '../../commons/utils/Utils.js';
import InventarioTarifariosGridRow from './InventarioTarifariosGridRow.jsx';
import InventarioTarifariosGridFiltros from './InventarioTarifariosGridFiltros.jsx';
import GridSearchResultsBadge from '../../commons/filters/GridSearchResultsBadge.jsx';
import GridPagination from '../../commons/grid/GridPagination.jsx';
import SortableCell from '../../commons/grid/SortableCell.jsx';
import { GridToolbar, GridToolbarButton } from '../../commons/grid/GridToolbar.jsx';
import { GridEmptyRow, GridLoadingRow } from '../../commons/grid/GridStateRow.jsx';
import Security from '../../commons/security/Security.js';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';

// Nombre, Descripción, Proveedores asociados, Última modificación y Acciones. Alimenta el colSpan
// de las filas de "cargando" y "sin resultados". No hay franja de estado: el grid lista solo activos.
const COLUMN_COUNT = 5;

const PAGINATION_INIT = { page: 0, count: 0, perPage: 10 };

const FILTER_QUERY_INIT = {
  nombre: '',
  descripcion: '',
  proveedores: [],
};

const SORT_QUERY_INIT = {
  nombre: 'asc',
  ultimaModificacion: '',
};

/** Depende solo de su flag: Tarifarios funciona tanto con el Inventario v3.0 como con el Pañol legacy. */
export const tarifariosHabilitado = () => ConfigBusiness.get('tarifarios.habilitado') === 'true';

const InventarioTarifariosGrid = (props) => {
  const intl = useIntl();

  const [loading, setLoading] = useState(false);
  const [redirectTo, setRedirectTo] = useState(null);

  const [searchActive, setSearchActive] = useState(false);
  const [pagination, setPagination] = useState({ ...PAGINATION_INIT });
  const [count, setCount] = useState(0);

  const [filterResetInputs, setFilterResetInputs] = useState(false);
  const [filterQuery, setFilterQuery] = useState({ ...FILTER_QUERY_INIT });

  const [sortQuery, setSortQuery] = useState({ ...SORT_QUERY_INIT });
  const [dropdownActive, setDropdownActive] = useState(false);

  const [rows, setRows] = useState([]);

  const [permissions, setPermissions] = useState({
    crear: false,
    visualizar: false,
    modificar: false,
    desactivar: false,
  });

  useEffect(() => {
    setLoading(true);
    if (Security.hasPermission('TARIFARIOS_LISTAR') && tarifariosHabilitado()) {
      securityPermission();
      fetchRows();
    } else {
      setRedirectTo('/error');
    }
    return () => {};
  }, [pagination, filterQuery, sortQuery]);

  const securityPermission = () => {
    setPermissions({
      crear: Security.hasPermission('TARIFARIOS_CREAR'),
      visualizar: Security.hasPermission('TARIFARIOS_VISUALIZAR'),
      modificar: Security.hasPermission('TARIFARIOS_MODIFICAR'),
      desactivar: Security.hasPermission('TARIFARIOS_DESACTIVAR'),
    });
  };

  const fetchRows = () => {
    Utils.getData(`/inventario/tarifarios/grid${getQuery()}`)
      .then((data) => {
        setRows((data && data.items) || []);
        setCount((data && data.pagination && data.pagination.count) || 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const getQuery = () => {
    let query = '?page=' + (pagination.page + 1); // +1 porque la API pagina en base 1 y el front en base 0
    query += '&perPage=' + pagination.perPage;
    query += '&nombre=' + encodeURIComponent(filterQuery.nombre || '');
    query += '&descripcion=' + encodeURIComponent(filterQuery.descripcion || '');
    query += '&proveedores=' + (filterQuery.proveedores || []).join(',');
    Object.keys(SORT_QUERY_INIT).forEach((key) => {
      if (sortQuery[key]) query += '&' + key + 'Sort=' + sortQuery[key];
    });
    return query;
  };

  const handleChangePage = (event, newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    setPagination({ ...pagination, perPage: parseInt(event.target.value, 10), page: 0 });
  };

  const requestSort = (key) => {
    let query = { ...sortQuery };
    let aux = sortQuery[key];
    Object.keys(SORT_QUERY_INIT).forEach((k) => {
      query[k] = '';
    });
    if (!aux) query[key] = 'asc';
    else if (aux === 'desc') query[key] = 'asc';
    else query[key] = 'desc';
    setPagination({ ...pagination, page: 0 });
    setSortQuery(query);
  };

  // Los dos resets de docs/design-system/grid-filter-panel.md: la "x" de la franja limpia la
  // búsqueda; "Restaurar" además devuelve el ordenamiento por defecto.
  const handleSearchClear = () => {
    setPagination(PAGINATION_INIT);
    setFilterQuery(FILTER_QUERY_INIT);
    setSearchActive(false);
    setFilterResetInputs(true);
  };

  const handleFilterReset = () => {
    setSortQuery(SORT_QUERY_INIT);
    setDropdownActive(false);
    handleSearchClear();
  };

  const handleFilterChange = () => {
    setPagination((prevState) => ({ ...prevState, page: 0 }));
  };

  return (
    <>
      {redirectTo && <Redirect push to={redirectTo} />}
      <div className="row mt-2">
        <div className="col-12">
          <div className="card">
            <div className="card-content collpase show">
              <div className="card-body card-dashboard">
                <div className="container-fluid">
                  <div className="row dt-icons">
                    <div className="col">
                      {permissions.crear && (
                        <StyledTooltip
                          title={intl.formatMessage({ id: 'Nuevo', defaultMessage: 'Nuevo' })}
                          arrow
                          placement="right"
                        >
                          <div
                            className="btn btn-primary box-shadow-2 btn-round btn-lg btn-dt-main round-icon"
                            onClick={() => setRedirectTo(props.match.url + '/add')}
                          >
                            <i className="ft-plus"></i>
                          </div>
                        </StyledTooltip>
                      )}
                      <div
                        className="btn btn-grey-blue box-shadow-2 btn-round btn-lg btn-dt-main round-icon"
                        onClick={() => setDropdownActive(!dropdownActive)}
                      >
                        {dropdownActive ? <UpIcon /> : <ExpandMoreIcon />}
                      </div>
                    </div>
                    <div className="col">
                      <GridSearchResultsBadge
                        visible={searchActive}
                        mostrados={rows.length}
                        total={count}
                        onReset={() => handleSearchClear()}
                      />
                    </div>
                    <div className="col">
                      <GridToolbar>
                        <GridToolbarButton
                          messageId="inventarioTarifarios.grid.tools_restore_filters.title"
                          label="Restaurar filtros y ordenamiento"
                          onClick={() => handleFilterReset()}
                        >
                          <img id="filterReset" className="filter-reset" src={LimpiarFiltros} alt="" />
                        </GridToolbarButton>
                      </GridToolbar>
                    </div>
                  </div>
                </div>

                <InventarioTarifariosGridFiltros
                  dropdownActive={dropdownActive}
                  setDropdownActive={setDropdownActive}
                  filterResetInputs={filterResetInputs}
                  setFilterResetInputs={setFilterResetInputs}
                  setFilterQuery={setFilterQuery}
                  setSearchActive={setSearchActive}
                  handleFilterChange={handleFilterChange}
                  loading={loading}
                />

                <TableContainer>
                  <Table aria-label="inventario tarifarios table" className="table-hover">
                    <TableHead>
                      <TableRow>
                        <SortableCell
                          field="nombre"
                          sort={sortQuery}
                          onSort={requestSort}
                          messageId="inventarioTarifarios.grid.column_nombre"
                          label="Nombre"
                        />
                        <SortableCell
                          field="descripcion"
                          messageId="inventarioTarifarios.grid.column_descripcion"
                          label="Descripción"
                        />
                        <SortableCell
                          field="proveedores"
                          messageId="inventarioTarifarios.grid.column_proveedores_asociados"
                          label="Proveedores asociados"
                        />
                        <SortableCell
                          field="ultimaModificacion"
                          sort={sortQuery}
                          onSort={requestSort}
                          messageId="inventarioTarifarios.grid.column_ultima_modificacion"
                          label="Última modificación"
                        />
                        <SortableCell
                          field="acciones"
                          align="center"
                          style={{ width: '140px' }}
                          messageId="inventarioTarifarios.grid.column_acciones"
                          label="Acciones"
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <GridLoadingRow visible={loading} columns={COLUMN_COUNT} />
                      {!loading &&
                        (rows.length > 0 ? (
                          rows.map((row) => (
                            <InventarioTarifariosGridRow
                              intl={intl}
                              key={'GridRow_' + row.id}
                              baseUrl={props.match.url}
                              row={row}
                              fetchRows={fetchRows}
                              permissions={permissions}
                            />
                          ))
                        ) : (
                          <GridEmptyRow
                            columns={COLUMN_COUNT}
                            messageId="inventarioTarifarios.grid.no_data_table"
                            label="No se encontraron resultados..."
                          />
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <GridPagination
                  count={count}
                  page={pagination.page}
                  perPage={pagination.perPage}
                  onPageChange={handleChangePage}
                  onPerPageChange={handleChangeRowsPerPage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InventarioTarifariosGrid;
